/**
 * Helper Utilities - Main Entry Point
 * Based on Helper.js from old codebase
 * Provides constants and re-exports all utility modules
 * 
 * This file maintains backward compatibility while organizing
 * utilities into modular, maintainable components
 */

import _ from 'lodash';
import Cookies from 'js-cookie';

// Re-export all utilities from modular files
export * from './format';
export * from './validation';
export * from './date';
export * from './random';
export * from './dom';
export * from './device';
export * from './array';
export * from './audio';
export * from './socket';
export { default as storage } from './storage';
export { C } from './constants';

/**
 * Simple EventEmitter implementation for browser
 * Compatible with Node.js EventEmitter API
 */
class EventEmitter {
  constructor() {
    this.events = {};
  }

  on(event, listener) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);
  }

  off(event, listener) {
    if (!this.events[event]) return;
    if (listener) {
      this.events[event] = this.events[event].filter(l => l !== listener);
    } else {
      delete this.events[event];
    }
  }

  emit(event, ...args) {
    if (!this.events[event]) return;
    this.events[event].forEach(listener => {
      try {
        listener(...args);
      } catch (error) {
        console.error(`Error in event listener for ${event}:`, error);
      }
    });
  }

  once(event, listener) {
    const onceWrapper = (...args) => {
      listener(...args);
      this.off(event, onceWrapper);
    };
    this.on(event, onceWrapper);
  }

  removeAllListeners(event) {
    if (event) {
      delete this.events[event];
    } else {
      this.events = {};
    }
  }

  setMaxListeners(n) {
    // Browser implementation - no limit needed
    return this;
  }
}

/**
 * Global Event Emitter (for compatibility with old code)
 * @type {EventEmitter}
 */
export const Event = new EventEmitter();
Event.setMaxListeners(0);

/**
 * APP Mode
 * @type {boolean}
 */
export const DEVELOPMENT = import.meta.env.DEV || true;

/**
 * APP Name
 * @type {string}
 */
export const BRAND = 'Jackopot';

/**
 * Site URL
 * @type {string}
 */
export const SITE_URL = DEVELOPMENT
  ? 'https://jackopot.com'
  : 'https://jackopot.com';

/**
 * Site (same as SITE_URL for compatibility)
 * @type {string}
 */
export const SITE = SITE_URL;

/**
 * General Socket Address
 * @type {string}
 */
export const SOCKET = DEVELOPMENT
  ? 'wss://api.jackopot.com'
  : 'wss://api.playcrash.com';

/**
 * General API Address
 * @type {string}
 */
export const API = DEVELOPMENT
  ? 'https://api.jackopot.com'
  : 'https://api.playcrash.com';

/**
 * Lodash (for compatibility)
 * @type {lodash}
 */
export const __ = _;

/**
 * Bulk Bet API Key
 * @type {string}
 */
export const BULK_BET_API = '';

/**
 * Show Bonus Wheel
 * @type {boolean}
 */
export const BONUS_WHEEL = false;

/**
 * Support Crisp Website ID
 * @type {string}
 */
export const CRISTP_ID = 'bca0c9b6-6897-4744-bf70-7315c4';

/**
 * Support Skype
 * @type {string}
 */
export const SUPPORT_SKYPE = 'https://join.skype.com/invite/';

/**
 * Giphy API Key
 * @type {string}
 */
export const GIPHY_API = '';

/**
 * Google Client ID for oAuth
 * @type {string}
 */
export const CLIENT_ID = DEVELOPMENT
  ? '869861485735-q8iaqbosrhdgjsp3rgiea4e7u447hsqr.apps.googleusercontent.com'
  : '869861485735-q8iaqbosrhdgjsp3rgiea4e7u447hsqr.apps.googleusercontent.com';

/**
 * Google Recaptcha v3 / Site Key
 * @type {string}
 */
export const RECAPTCHA = DEVELOPMENT
  ? '6LcErBMaAAAAAEyCPnasQb5W1iESJ0KjlBOkz3fs'
  : '6Lc-phMaAAAAAD0RpGuPK_mtSPgHh3IvffzMmOqt';

/**
 * Default Avatar
 * @type {string}
 */
export const defaultAvatar = '/assets/images/avatar.png';

/**
 * Making Game Object (for exchange rates)
 * Populated by socket connection
 * @type {Object}
 */
export const Game = Object.create(null);

// Make Game available globally for format utilities
if (typeof window !== 'undefined') {
  window.Game = Game;
}

/**
 * Making User Object
 * Populated by user data
 * @type {Object}
 */
export const User = Object.create(null);

/**
 * Get User ID from Cookie
 * @returns {number|string}
 */
export function getUID() {
  return parseFloat(Cookies.get('uid')) || '';
}

/**
 * SetTimeOut Promise
 * @param {number} ms
 * @returns {Promise<void>}
 */
export const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Check is Function
 * @param {*} functionToCheck
 * @returns {boolean}
 */
export function isFunction(functionToCheck) {
  return (
    functionToCheck &&
    {}.toString.call(functionToCheck) === '[object Function]'
  );
}

/**
 * Security check (placeholder)
 * @returns {boolean}
 */
export function chkd() {
  // Security checks can be added here
  return true;
}
